import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button, Divider, Dropdown, Tooltip } from '@bynder/design-system';
import {
    IconAdd,
    IconBuildShape,
    IconCircle,
    IconImage,
    IconRectangle,
    IconTextField,
    IconVideoTemplate,
} from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import useElementCreator from 'packages/pages/editor/useElementCreator';
import useCreateElementShortcuts from 'packages/hooks/shortcuts/useCreateElementShortcuts';
import useAccessRights from 'packages/hooks/useAccessRights';
import usePresetShortcuts from 'packages/hooks/shortcuts/usePresetShortcut';
import { WithShortcutLabel } from 'packages/common/WithShortcutLabel';
import useComponentOverview from 'packages/common/Components/ComponentOverview';
import features from '~/configs/features';
import PresetModal from '~/common/editor/presets/PresetsModal';
import ContentPickerModal from 'packages/pages/editor/ContentPickerModal';
import { isImageOrVideo } from '~/common/editor/helpers';
import usePresets from '~/common/editor/presets/usePresets';
import { AddElementWrapper } from './AddLayerButton.styled';

const EMPTY_SELECTED_OBJECT = {};

const AddLayerButton = () => {
    const elementCreator = useElementCreator();
    const [assetType, setAssetType] = useState('');
    const [showContentSelectorModal, setShowContentSelectorModal] = useState(false);
    const [showPresetModal, setShowPresetModal] = useState(false);
    const { applyPreset } = usePresets();
    const { translate } = useTranslate();

    const { isPlatformAllowed } = useAccessRights();
    const hasComponentGalleryFeature = isPlatformAllowed([features.COMPONENT_GALLERY]);

    const { toggleComponentOverview } = useComponentOverview({
        onApplySuccess: () => {},
    });

    const handleToggleComponentOverview = useCallback(() => {
        if (hasComponentGalleryFeature) {
            toggleComponentOverview();
        } else {
            setShowPresetModal(!showPresetModal);
        }
    }, [toggleComponentOverview, showPresetModal, hasComponentGalleryFeature]);

    const handleAddPreset = useCallback(
        (selectedPreset, isUniqueContentProperties) => {
            // Temporary solution
            const finalSelectedElement = selectedPreset;
            finalSelectedElement.elements[Object.keys(finalSelectedElement.elements)].name = selectedPreset.name;

            applyPreset(finalSelectedElement, isUniqueContentProperties);
            setShowPresetModal(false);
        },
        [applyPreset],
    );

    const handleConfirmSelection = useCallback(
        (source) => {
            elementCreator.createImageElement(source);
            setShowContentSelectorModal(false);
        },
        [elementCreator.createImageElement],
    );

    const openContentPicker = useCallback(() => {
        setAssetType('IMAGE');
        setShowContentSelectorModal(true);
    }, []);

    useCreateElementShortcuts(elementCreator, openContentPicker);
    usePresetShortcuts({ toggleModal: handleToggleComponentOverview });

    const contentPickerModalProps = useMemo(
        () => ({
            type: assetType,
            show: showContentSelectorModal,
            selectedObject: EMPTY_SELECTED_OBJECT,
            frameRate: undefined,
            toggleModal: setShowContentSelectorModal,
            confirmSelection: handleConfirmSelection,
            resizableRange: true,
            isEditorPage: true,
        }),
        [assetType, showContentSelectorModal, isImageOrVideo],
    );

    return (
        <div>
            {showPresetModal && (
                <PresetModal
                    toggleModal={handleToggleComponentOverview}
                    confirmSelection={handleAddPreset}
                    show={showPresetModal}
                />
            )}
            <Dropdown
                position="bottom-left"
                trigger={({ isOpen, ...triggerProps }) => (
                    <Tooltip content={translate('editor.layers.sidebar.newLayer')}>
                        <AddElementWrapper>
                            <Button variant="clean" className="add--layer-btn" icon={<IconAdd />} {...triggerProps} />
                        </AddElementWrapper>
                    </Tooltip>
                )}
            >
                <Dropdown.Item icon={<IconImage />} onClick={openContentPicker}>
                    <WithShortcutLabel shortcut={['i']}>{translate('editor.layers.sidebar.image')}</WithShortcutLabel>
                </Dropdown.Item>
                <Dropdown.Item icon={<IconTextField />} onClick={elementCreator.createTextElement}>
                    <WithShortcutLabel shortcut={['t']}>{translate('editor.layers.sidebar.text')}</WithShortcutLabel>
                </Dropdown.Item>
                <Dropdown.Item
                    icon={<IconBuildShape />}
                    submenu={
                        <>
                            <Dropdown.Item icon={<IconRectangle />} onClick={elementCreator.createRectangleElement}>
                                <WithShortcutLabel shortcut={['r']}>
                                    {translate('editor.layers.sidebar.rectangle')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                            <Dropdown.Item icon={<IconCircle />} onClick={elementCreator.createEllipseElement}>
                                <WithShortcutLabel shortcut={['e']}>
                                    {translate('editor.layers.sidebar.ellipse')}
                                </WithShortcutLabel>
                            </Dropdown.Item>
                        </>
                    }
                >
                    {translate('editor.layers.sidebar.shape')}
                </Dropdown.Item>
                <Divider />
                <Dropdown.Item icon={<IconVideoTemplate />} onClick={handleToggleComponentOverview}>
                    <WithShortcutLabel shortcut={['p']}>
                        {hasComponentGalleryFeature
                            ? translate('editor.layers.sidebar.components')
                            : translate('editor.layers.sidebar.presets')}
                    </WithShortcutLabel>
                </Dropdown.Item>
            </Dropdown>
            {showContentSelectorModal && <ContentPickerModal {...contentPickerModalProps} />}
        </div>
    );
};

export default memo(AddLayerButton);
